/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import { getUsersActionsLogs as getUsersReportSearchLogs } from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { mapUsersReportRows } from './reportTypeOptions'
import AuditPrintOccurrenceFilters from './AuditPrintOccurrenceFilters'
import {
  PrintOccurrenceSearchLogResponse,
  PrintOccurrenceSearchLogRow,
} from '../interfaces'

const limit = 15

export default function ListAllPrintOccurrenceReport() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const action = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const IdDispatch = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandUsersReports = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] =
    useState<PrintOccurrenceSearchLogRow | null>(null)

  const columns: readonly GridColDef[] = [
    {
      field: 'user:representation_name',
      headerName: translate('Usuário'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'station',
      headerName: translate('Estação'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'detail',
      headerName: translate('Ocorrência'),
      flex: 2,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'created_at',
      headerName: translate('Realizado em'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip title={translate('Visualizar')}>
            <IconButton
              onClick={() =>
                handleView(params.row as PrintOccurrenceSearchLogRow)
              }
              size="small"
              aria-label={translate('Visualizar')}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    action.set(null)
    IdDispatch.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandUsersReports.set(false)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const searchLogData: PrintOccurrenceSearchLogResponse =
      await getUsersReportSearchLogs({
        user_id: userId.get() || undefined,
        dispatch_id: IdDispatch.get() || undefined,
        start_date: startDate,
        finish_date: finishDate,
        action: action.get(),
        page: page,
        limit: pageSize,
        orders:
          orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
      }).finally(() => {
        loadingButton.set(false)
        expandUsersReports.set(true)
      })

    setRows(mapUsersReportRows(searchLogData.data, page, pageSize))

    setRowCount(searchLogData.rowsCount)
  }

  const handleView = (row: PrintOccurrenceSearchLogRow) => {
    setSelectedRow(row)
    setOpenModal(true)
  }

  const clickOutModalClose = (event: unknown, reason?: string) => {
    if (reason !== 'backdropClick') {
      setOpenModal(false)
    }
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditPrintOccurrenceFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          /* station filter removed */
          IdDispatch={IdDispatch}
          initialPeriod={initialPeriod}
          action={action}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
          
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() => expandUsersReports.set(!expandUsersReports.get())}
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandUsersReports.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandUsersReports.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openModal}
        onClose={clickOutModalClose}
        disableEscapeKeyDown={true}
      >
        <DialogTitle
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          {translate('Actions Performed')}
          <IconButton aria-label="close" onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 5 }}>
          {selectedRow && (
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <strong>{translate('Usuário')}:</strong>
                <div>{selectedRow['user:representation_name']}</div>
              </div>
              <div style={{ flex: 1 }}>
                <strong>{translate('Estação')}:</strong>
                <div>{selectedRow.station ?? ''}</div>
              </div>
              <div style={{ flex: 2 }}>
                <strong>{translate('Ocorrência')}:</strong>
                <div>{selectedRow.detail ?? ''}</div>
              </div>
              <div style={{ flex: 1 }}>
                <strong>{translate('Realizado em')}:</strong>
                <div>
                  {dayjs(selectedRow.created_at).format('DD/MM/YYYY HH:mm')}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
